/**
 * Role Constants and Types
 *
 * This is the single source of truth for all role-based logic in the PWA.
 * All components, composables, and utilities should import from this file
 * rather than using hardcoded role strings.
 *
 * Role Hierarchy (by priority):
 * - admin-super (100): Full platform access, can manage all users and settings
 * - admin-manager (80): Can manage employees, customers, and bookings
 * - service-provider (40): Can manage own availability and view assigned jobs
 * - customer (10): Can create and manage own bookings
 */

/**
 * Role string constants for user authentication (JWT format with hyphens)
 * Use `as const` to allow deriving types from this object
 */
export const ROLES = {
  CUSTOMER: 'customer',
  ADMIN_SUPER: 'admin-super',
  ADMIN_MANAGER: 'admin-manager',
  SERVICE_PROVIDER: 'service-provider',
} as const

/**
 * GraphQL Employee role constants (database format with underscores)
 * These match the UserRole type from GraphQL schema
 */
export const EMPLOYEE_ROLES = {
  ADMIN_SUPER: 'admin_super',
  ADMIN_MANAGER: 'admin_manager',
  SERVICE_PROVIDER: 'service_provider',
} as const

/**
 * Derive Role type from ROLES constant
 * This ensures types and strings are always in sync
 * @example
 * const userRole: Role = ROLES.ADMIN_SUPER  // ✓ valid
 * const userRole: Role = 'invalid'           // ✗ type error
 */
export type Role = (typeof ROLES)[keyof typeof ROLES]

/**
 * Derive EmployeeRole type from EMPLOYEE_ROLES constant
 * Used for GraphQL Employee.roles field
 * @example
 * const empRole: EmployeeRole = EMPLOYEE_ROLES.SERVICE_PROVIDER  // ✓ valid
 * const empRole: EmployeeRole = 'invalid'                         // ✗ type error
 */
export type EmployeeRole = (typeof EMPLOYEE_ROLES)[keyof typeof EMPLOYEE_ROLES]

/**
 * Human-readable role names for UI display
 * Maps role strings to display-friendly names
 * @example
 * const displayName = ROLE_NAMES[ROLES.CUSTOMER]  // 'Customer'
 */
export const ROLE_NAMES: Record<Role, string> = {
  [ROLES.CUSTOMER]: 'Customer',
  [ROLES.ADMIN_SUPER]: 'Super Admin',
  [ROLES.ADMIN_MANAGER]: 'Manager',
  [ROLES.SERVICE_PROVIDER]: 'Service Provider',
}

/**
 * Role priority levels for role comparison logic
 * Higher numbers = higher privilege
 *
 * Used for:
 * - Determining if one role outranks another
 * - Sorting users by permission level
 * - Access control comparisons
 *
 * @example
 * const isHigherPriority = ROLE_PRIORITIES[roleA] > ROLE_PRIORITIES[roleB]
 */
export const ROLE_PRIORITIES: Record<Role, number> = {
  [ROLES.ADMIN_SUPER]: 100,
  [ROLES.ADMIN_MANAGER]: 80,
  [ROLES.SERVICE_PROVIDER]: 40,
  [ROLES.CUSTOMER]: 10,
}

/**
 * Type for role display names
 * Useful for type-safe operations on role names
 */
export type RoleName = (typeof ROLE_NAMES)[Role]
